import { sendFriendRecommendation } from '@/api/endpoints/friend-recommendation';
import { AvatarGroup } from '@/components/display/avatar-group';
import { confirm } from '@/components/ui/confirmation-dialog';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { toast } from '@/components/ui/toast';
import {
  useAddComment,
  useComments,
  useDeleteComment,
} from '@/features/activity/hooks/use-comments';
import { useDeleteCheckIn } from '@/features/activity/hooks/use-delete-check-in';
import type { RecommendFriend } from '@/features/check-in/types';
import { FriendPickerSheet } from '@/features/friend-recommendations/components/friend-picker-sheet';
import { SaveToListSheet } from '@/features/lists/components/save-to-list-sheet';
import { useAuth } from '@/hooks/use-auth';
import { useResponsiveBreakpoint } from '@/hooks/use-responsive-breakpoint';
import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import type { FeedItem, FeedItemComment } from '@/types/feed';
import { formatRelativeTime } from '@/utils/format-time';
import { likeHaptic } from '@/utils/haptics';
import { ratingKeyToIndex } from '@/utils/rating-utils';
import { Ionicons } from '@expo/vector-icons';
import { Link, router } from 'expo-router';
import { memo, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  NativeSyntheticEvent,
  Pressable,
  TextLayoutEventData,
  View,
} from 'react-native';
import { ImageViewer } from '../../../components/display/image-viewer';
import { prefetchViewerPhotos } from '../../../components/display/image-viewer.utils';
import { PhotoGrid } from '../../../components/display/photo-grid';
import { CommentInputBar } from './comment-input-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Rating } from '../../../components/ui/rating';
import { applyOptimisticLike } from '../utils/optimistic-like';
import { LikesModal } from './likes-modal';

interface ActivityCardProps {
  item: FeedItem;
  onLike?: (checkInId: string, currentlyLiked: boolean) => void;
}

function ActivityCardComponent({ item, onLike }: ActivityCardProps) {
  const theme = useAppTheme();
  const breakpoint = useResponsiveBreakpoint();
  const showActionLabels = breakpoint === 'desktop' || breakpoint === 'large-desktop';
  const { user: currentUser } = useAuth();
  const { deleteCheckIn, restoreCheckIn } = useDeleteCheckIn();
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [likesModalVisible, setLikesModalVisible] = useState(false);
  const [saveSheetVisible, setSaveSheetVisible] = useState(false);
  const [recommendSheetVisible, setRecommendSheetVisible] = useState(false);
  const inputRef = useRef<any>(null);

  // Local optimistic like state so the heart flips on the same synchronous frame
  // as the haptic, instead of waiting for the React Query cache round-trip.
  // Reconciles with the cache value (including error rollback) by adjusting state
  // during render whenever the server-truth value changes -- React's recommended
  // pattern for a prop-derived state, avoiding a setState-in-effect cascade.
  const [optimisticLiked, setOptimisticLiked] = useState(item.userHasLiked);
  const [prevServerLiked, setPrevServerLiked] = useState(item.userHasLiked);
  if (item.userHasLiked !== prevServerLiked) {
    setPrevServerLiked(item.userHasLiked);
    setOptimisticLiked(item.userHasLiked);
  }

  // Everything the like tap affects -- heart, count, and avatars -- derives from a
  // single optimistic recompute so they all flip together on the same frame. Falls
  // back to the raw item once the cache catches up (optimisticLiked === server truth).
  const display =
    optimisticLiked === item.userHasLiked
      ? item
      : { ...item, ...applyOptimisticLike(item, currentUser, item.userHasLiked) };

  const handleLikePress = () => {
    const wasLiked = optimisticLiked;
    if (!wasLiked) likeHaptic();
    setOptimisticLiked(!wasLiked);
    onLike?.(item.checkIn.id, wasLiked);
  };

  const { addComment, isSubmitting } = useAddComment();
  const { deleteComment } = useDeleteComment();

  const {
    data: commentsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingComments,
  } = useComments(item.checkIn.id, { enabled: expanded });

  const allComments = commentsData?.pages.flatMap((page) => page.items) ?? [];

  const isOwner = currentUser?.id === item.checkIn.userId;
  const ratingIndex = item.rating ? ratingKeyToIndex(item.rating.key) : null;
  const timestamp = formatRelativeTime(
    item.checkIn.occurredAt || item.checkIn.createdAt
  );
  // Content owned by the "Flavrly User" sentinel (a deleted account's anonymized
  // content) renders view-only: the byline is non-navigable and interaction
  // affordances (like/comment/recommend/save) are hidden.
  const isSentinelAuthor = !!item.user.isSentinel;
  const authorName = isSentinelAuthor
    ? 'Flavrly User'
    : item.user.displayName || item.user.username || 'User';

  // Photos enriched with per-photo caption context for the lightbox.
  const viewerPhotos = useMemo(
    () =>
      item.photos.map((p) => ({
        ...p,
        caption: {
          checkInId: item.checkIn.id,
          dishName: item.menuItem.name,
          businessName: item.menuItem.businessName,
          author: {
            name: item.user.displayName ?? item.user.username ?? 'Someone',
            avatarUrl: item.user.avatarUrl,
          },
          createdAt: item.checkIn.occurredAt ?? item.checkIn.createdAt,
          ratingLabel: item.rating?.label,
          ratingKey: item.rating?.key,
        },
      })),
    [item]
  );

  // Warm the full-size images on touch-down so the viewer opens with them ready.
  const handlePhotoPressIn = () => {
    prefetchViewerPhotos(viewerPhotos);
  };

  const handlePhotoPress = (index: number) => {
    setViewerIndex(index);
    setViewerVisible(true);
  };

  const handleDeleteCheckIn = async () => {
    const confirmed = await confirm({
      title: 'Delete check-in?',
      description:
        'Are you sure you want to delete this check-in? This action cannot be undone.',
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      deleteCheckIn(item.checkIn.id);
      const toastId = toast.success('Check-in deleted', {
        action: {
          label: 'Undo',
          onPress: () => {
            toast.dismiss(toastId);
            restoreCheckIn(item.checkIn.id);
            toast.success('Check-in restored');
          },
        },
      });
    }
  };

  const handleCommentToggle = () => {
    setExpanded((prev) => !prev);
    if (!expanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSendComment = async () => {
    const trimmed = commentText.trim();
    if (!trimmed || isSubmitting) return;

    setCommentText('');
    try {
      await addComment(item.checkIn.id, trimmed);
    } catch {
      // Mutation handles rollback
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    const confirmed = await confirm({
      title: 'Delete comment?',
      description:
        'Are you sure you want to delete this comment? This action cannot be undone.',
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      deleteComment(commentId, item.checkIn.id);
    }
  };

  const previewComments = item.recentComments;
  const hasMoreComments = item.commentsCount > previewComments.length;

  return (
    <View className="rounded-md py-4 mb-4 shadow-sm bg-card border border-border">
      {/* User info and timestamp */}
      <View className="flex-row items-center mb-3 px-4">
        {(() => {
          const avatarInner = (
            <UserAvatar
              uri={item.user.avatarUrl}
              name={isSentinelAuthor ? null : item.user.displayName || item.user.username}
              size={40}
              accessibilityLabel={`${authorName}'s profile photo`}
            />
          );
          return isSentinelAuthor ? (
            <View>{avatarInner}</View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`View ${authorName}'s profile`}
              hitSlop={{ top: 2, bottom: 2, left: 2, right: 2 }}
              onPress={() => {
                router.push({
                  pathname: '/(root)/profile/[username]',
                  params: { username: item.user.username || item.user.id },
                });
              }}
            >
              {avatarInner}
            </Pressable>
          );
        })()}
        <View className="ml-3 flex-1">
          {isSentinelAuthor ? (
            <Text className="font-medium text-base text-foreground">
              {authorName}
            </Text>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`View ${authorName}'s profile`}
              hitSlop={{ top: 10, bottom: 2, left: 4 }}
              onPress={() => {
                router.push({
                  pathname: '/(root)/profile/[username]',
                  params: { username: item.user.username || item.user.id },
                });
              }}
            >
              <Text className="font-medium text-base text-foreground">
                {authorName}
              </Text>
            </Pressable>
          )}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`View this check-in, ${timestamp}`}
            hitSlop={{ top: 2, bottom: 10, left: 4 }}
            onPress={() => {
              router.push({
                pathname: '/(root)/check-in/[id]',
                params: { id: item.checkIn.id },
              });
            }}
          >
            <Text className="text-sm text-muted-foreground">
              {timestamp}
            </Text>
          </Pressable>
        </View>
        {isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Pressable
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="More options"
              >
                <Ionicons
                  name="ellipsis-horizontal"
                  size={20}
                  className="text-muted-foreground"
                />
              </Pressable>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                destructive
                icon={
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    className="text-error"
                  />
                }
                onPress={handleDeleteCheckIn}
              >
                <Text className="text-sm">Delete Check-in</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </View>

      {/* Activity content + inline rating */}
      <View className="flex-row items-center mb-2 px-4">
        <Text className="flex-1 text-muted-foreground text-base">
          Checked in
          <Link href={`/menu-item/${item.menuItem.id}`}>
            <Text className="font-bold text-foreground"> {item.menuItem.name} </Text>
          </Link>
          at
          <Link href={`/business/${item.checkIn.businessId}`}>
            <Text className="font-bold text-foreground">
              {' '}
              {item.menuItem.businessName || item.checkIn.businessName || 'a restaurant'}
            </Text>
          </Link>
        </Text>
        {ratingIndex !== null && (
          <Rating rating={ratingIndex} />
        )}
      </View>

      {/* Photo grid */}
      {item.photos.length > 0 && (
        <View className="mt-2 mb-2">
          <PhotoGrid
            photos={item.photos}
            onPhotoPress={handlePhotoPress}
            onPhotoPressIn={handlePhotoPressIn}
          />
        </View>
      )}

      {/* Review text */}
      {item.review && (
        <View className="rounded-lg overflow-hidden mt-2 mb-2 px-4 py-2">
          <Text className="text-base leading-6 text-foreground/70">
            {item.review.content}
          </Text>
        </View>
      )}

      {/* Action buttons -- hidden for sentinel-owned (Flavrly User) content */}
      {!isSentinelAuthor && (
      <View className="flex-row justify-between pt-3 mt-3 border-t border-border px-4">
        <View className="flex-row items-center">
          <Pressable
            className="flex-row items-center"
            onPress={handleLikePress}
            hitSlop={{ top: 12, bottom: 12 }}
            accessibilityRole="button"
            accessibilityLabel={`${display.userHasLiked ? 'Liked' : 'Like'}, ${display.likesCount} ${display.likesCount === 1 ? 'like' : 'likes'}`}
          >
            <Ionicons
              name={display.userHasLiked ? 'heart' : 'heart-outline'}
              size={20}
              className={display.userHasLiked ? "text-coral" : "text-muted-foreground"}
            />
          </Pressable>
          {display.likesCount > 0 && display.recentLikers.length > 0 ? (
            <Pressable
              className="flex-row items-center ml-2"
              onPress={() => setLikesModalVisible(true)}
              hitSlop={{ top: 12, bottom: 12 }}
              accessibilityRole="button"
              accessibilityLabel={`${display.likesCount} ${display.likesCount === 1 ? 'like' : 'likes'}, tap to view`}
            >
              <AvatarGroup
                users={display.recentLikers.map((l) => ({ id: l.id, avatarUrl: l.avatarUrl }))}
                totalCount={display.likesCount}
                size={20}
                overlapPx={6}
              />
              <Text
                className={cn("ml-1.5 text-sm", display.userHasLiked ? "text-coral" : "text-muted-foreground")}
                accessible={false}
              >
                {display.likesCount}
              </Text>
            </Pressable>
          ) : display.likesCount > 0 ? (
            <Text
              className={cn("ml-2 text-xs", display.userHasLiked ? "text-coral" : "text-muted-foreground")}
              accessible={false}
            >
              {display.likesCount}
            </Text>
          ) : showActionLabels ? (
            <Text
              className="ml-2 text-xs text-muted-foreground"
              accessible={false}
            >
              Like
            </Text>
          ) : null}
        </View>
        <Pressable
          className="flex-row items-center"
          onPress={handleCommentToggle}
          hitSlop={{ top: 12, bottom: 12 }}
          accessibilityRole="button"
          accessibilityLabel={`Comment, ${item.commentsCount} ${item.commentsCount === 1 ? 'comment' : 'comments'}`}
        >
          <Ionicons
            name="chatbubble-outline"
            size={20}
            className="text-muted-foreground"
          />
          {(showActionLabels || item.commentsCount > 0) && (
            <Text className="ml-2 text-xs text-muted-foreground" accessible={false}>
              {item.commentsCount > 0 ? item.commentsCount : 'Comment'}
            </Text>
          )}
        </Pressable>
        <Pressable
          className="flex-row items-center"
          onPress={() => setRecommendSheetVisible(true)}
          hitSlop={{ top: 12, bottom: 12 }}
          accessibilityRole="button"
          accessibilityLabel="Recommend to friend"
        >
          <Ionicons
            name="paper-plane-outline"
            size={20}
            className="text-muted-foreground"
          />
          {showActionLabels && (
            <Text className="ml-2 text-xs text-muted-foreground" accessible={false}>
              Recommend
            </Text>
          )}
        </Pressable>
        <Pressable
          className="flex-row items-center"
          onPress={() => setSaveSheetVisible(true)}
          hitSlop={{ top: 12, bottom: 12 }}
          accessibilityRole="button"
          accessibilityLabel={item.listSaveCount > 0
            ? `Saved to ${item.listSaveCount} ${item.listSaveCount === 1 ? 'list' : 'lists'}`
            : 'Save to list'}
        >
          <View>
            <Ionicons
              name={item.listSaveCount > 0 ? 'bookmark' : 'bookmark-outline'}
              size={20}
              className={item.listSaveCount > 0 ? 'text-muted-foreground' : 'text-muted-foreground'}
            />
            {item.listSaveCount > 0 && (
              <View className="absolute inset-0 items-center justify-center" style={{ marginTop: -3 }}>
                <Text className="text-xs font-bold text-primary-foreground">
                  {item.listSaveCount}
                </Text>
              </View>
            )}
          </View>
          {showActionLabels && (
            <Text
              className="ml-2 text-xs text-muted-foreground"
              accessible={false}
            >
              {item.listSaveCount > 0 ? 'Saved' : 'Save'}
            </Text>
          )}
        </Pressable>
      </View>
      )}

      {/* Inline comments section -- hidden for sentinel-owned (Flavrly User) content */}
      {!isSentinelAuthor && (previewComments.length > 0 || expanded) && (
        <View className="mt-3 px-4">
          {/* Preview comments (always visible when present) */}
          {!expanded && previewComments.length > 0 && (
            <View>
              {previewComments.map((comment) => (
                <PreviewComment
                  key={comment.id}
                  comment={comment}
                  isOwn={currentUser?.id === comment.userId}
                  onDelete={handleDeleteComment}
                />
              ))}
              {hasMoreComments && (
                <Pressable onPress={handleCommentToggle}>
                  <Text className="text-sm mt-1 text-muted-foreground">
                    View all {item.commentsCount} comments
                  </Text>
                </Pressable>
              )}
            </View>
          )}

          {/* Expanded comments (full list) */}
          {expanded && (
            <View>
              {isLoadingComments ? (
                <View className="py-3 items-center">
                  <ActivityIndicator color={theme.colors.primary} />
                </View>
              ) : allComments.length === 0 ? (
                <Text className="text-sm py-2 text-muted-foreground">
                  No comments yet. Be the first!
                </Text>
              ) : (
                <>
                  {hasNextPage && (
                    <Pressable
                      onPress={() => fetchNextPage()}
                      className="py-2 items-center"
                    >
                      {isFetchingNextPage ? (
                        <ActivityIndicator
                          size="small"
                          color={theme.colors.primary}
                        />
                      ) : (
                        <Text className="text-sm font-medium text-primary">
                          Load earlier comments
                        </Text>
                      )}
                    </Pressable>
                  )}
                  {allComments.map((comment) => (
                    <ExpandedComment
                      key={comment.id}
                      comment={comment}
                      isOwn={currentUser?.id === comment.userId}
                      onDelete={handleDeleteComment}
                    />
                  ))}
                </>
              )}
            </View>
          )}
        </View>
      )}

      {!isSentinelAuthor && expanded && (
        <CommentInputBar
          ref={inputRef}
          avatarUrl={currentUser?.image}
          value={commentText}
          onChangeText={setCommentText}
          onSend={handleSendComment}
          isSending={isSubmitting}
        />
      )}

      {/* Image viewer lightbox */}
      {viewerVisible && (
        <ImageViewer
          photos={viewerPhotos}
          initialIndex={viewerIndex}
          visible={viewerVisible}
          onClose={() => setViewerVisible(false)}
        />
      )}

      {/* Likes modal */}
      <LikesModal
        visible={likesModalVisible}
        onClose={() => setLikesModalVisible(false)}
        checkInId={item.checkIn.id}
      />

      {/* Save to list sheet */}
      <SaveToListSheet
        itemType="menu_item"
        itemId={item.menuItem.id}
        visible={saveSheetVisible}
        onClose={() => setSaveSheetVisible(false)}
      />

      {/* Recommend to friend sheet */}
      <FriendPickerSheet
        menuItemId={item.menuItem.id}
        visible={recommendSheetVisible}
        onClose={() => setRecommendSheetVisible(false)}
        onConfirm={(selected: RecommendFriend[]) => {
          for (const friend of selected) {
            sendFriendRecommendation({
              recipient_id: friend.id,
              menu_item_id: item.menuItem.id,
            }).catch((err) => {
              console.warn('[ActivityCard] Recommendation failed:', err);
            });
          }
          if (selected.length > 0) {
            toast.success(
              selected.length === 1
                ? 'Recommendation sent'
                : `${selected.length} recommendations sent`
            );
          }
        }}
      />
    </View>
  );
}

function PreviewComment({
  comment,
  isOwn,
  onDelete,
}: {
  comment: FeedItemComment;
  isOwn: boolean;
  onDelete: (id: string) => void;
}) {
  const isSentinel = !!comment.user.isSentinel;
  const displayName = isSentinel
    ? 'Flavrly User'
    : comment.user.displayName || comment.user.username || 'User';

  const navigateToProfile = () => {
    router.push({
      pathname: '/(root)/profile/[username]',
      params: { username: comment.user.username || comment.userId },
    });
  };

  const avatarInner = (
    <UserAvatar
      uri={comment.user.avatarUrl}
      name={isSentinel ? null : comment.user.displayName || comment.user.username}
      size={20}
      accessibilityLabel={`${displayName}'s profile photo`}
    />
  );

  return (
    <View
      className="flex-row items-start mb-1 px-2 py-1.5 bg-surface-secondary"
      style={{ borderRadius: 6 }}
    >
      {isSentinel ? (
        <View>{avatarInner}</View>
      ) : (
        <Pressable onPress={navigateToProfile}>{avatarInner}</Pressable>
      )}
      <View className="flex-1 ml-1.5">
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center flex-1">
            <Text
              className="font-semibold text-sm text-foreground"
              onPress={isSentinel ? undefined : navigateToProfile}
            >
              {displayName}
            </Text>
            <Text className="text-xs ml-1.5 text-muted-foreground">
              · {formatRelativeTime(comment.createdAt)}
            </Text>
          </View>
          {isOwn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Pressable
                  hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}
                  accessibilityRole="button"
                  accessibilityLabel="Comment options"
                >
                  <Ionicons
                    name="ellipsis-horizontal"
                    size={14}
                    className="text-muted-foreground"
                  />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  destructive
                  icon={<Ionicons name="trash-outline" size={14} className="text-error" />}
                  onPress={() => onDelete(comment.id)}
                >
                  <Text className="text-sm">Delete</Text>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </View>
        <TruncatableText content={comment.content} />
      </View>
    </View>
  );
}

function ExpandedComment({
  comment,
  isOwn,
  onDelete,
}: {
  comment: { id: string; userId: string; content: string; createdAt: string; user: { displayName?: string; username?: string; avatarUrl?: string | null; isSentinel?: boolean } };
  isOwn: boolean;
  onDelete: (id: string) => void;
}) {
  const isSentinel = !!comment.user.isSentinel;
  const displayName = isSentinel
    ? 'Flavrly User'
    : comment.user.displayName || comment.user.username || 'User';

  const navigateToProfile = () => {
    router.push({
      pathname: '/(root)/profile/[username]',
      params: { username: comment.user.username || comment.userId },
    });
  };

  const avatarInner = (
    <UserAvatar
      uri={comment.user.avatarUrl}
      name={isSentinel ? null : comment.user.displayName || comment.user.username}
      size={28}
      accessibilityLabel={`${displayName}'s profile photo`}
    />
  );

  return (
    <View
      className="flex-row mb-3 px-2.5 py-2 bg-surface-secondary"
      style={{ borderRadius: 8 }}
    >
      {isSentinel ? (
        <View>{avatarInner}</View>
      ) : (
        <Pressable onPress={navigateToProfile}>{avatarInner}</Pressable>
      )}
      <View className="flex-1 ml-2">
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center">
            <Text
              className="font-semibold text-sm text-foreground"
              onPress={isSentinel ? undefined : navigateToProfile}
            >
              {displayName}
            </Text>
            <Text className="text-xs ml-2 text-muted-foreground">
              {formatRelativeTime(comment.createdAt)}
            </Text>
          </View>
          {isOwn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Pressable
                  hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}
                  accessibilityRole="button"
                  accessibilityLabel="Comment options"
                >
                  <Ionicons
                    name="ellipsis-horizontal"
                    size={14}
                    className="text-muted-foreground"
                  />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  destructive
                  icon={<Ionicons name="trash-outline" size={14} className="text-error" />}
                  onPress={() => onDelete(comment.id)}
                >
                  <Text className="text-sm">Delete</Text>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </View>
        <TruncatableText content={comment.content} />
      </View>
    </View>
  );
}

function TruncatableText({
  content,
  maxLines = 5,
}: {
  content: string;
  maxLines?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);

  const handleTextLayout = (e: NativeSyntheticEvent<TextLayoutEventData>) => {
    if (!expanded && e.nativeEvent.lines.length >= maxLines) {
      setNeedsTruncation(true);
    }
  };

  return (
    <>
      <Text
        className="text-sm mt-0.5 text-foreground"
        numberOfLines={expanded ? undefined : maxLines}
        onTextLayout={handleTextLayout}
      >
        {content}
      </Text>
      {needsTruncation && (
        <Pressable onPress={() => setExpanded(!expanded)}>
          <Text className="text-xs mt-0.5 text-primary">
            {expanded ? 'Show less' : 'Show more'}
          </Text>
        </Pressable>
      )}
    </>
  );
}

/**
 * Memoized so a parent re-render (e.g. profile hover state, a sibling card's
 * like) doesn't repaint every mounted card. `item` is a stable cache reference
 * from React Query; `onLike` must be a stable callback for the memo to hold.
 */
export const ActivityCard = memo(ActivityCardComponent);

export default ActivityCard;
